import React, { useEffect, useState } from "react";
import { FaTrash, FaMinus, FaPlus } from "react-icons/fa";
import { useLocation } from "react-router-dom";

import { useCart } from "../context/CartContext";
import { useOrder } from "../context/OrderContext";
import { useModal } from "../context/ConfirmModalContext";

import { DashboardHeader } from "../components/DashboardHeader";
import FullPageSpinner from "../components/FullPageSpinner";
import OrderDetailsModal from "../Modal/orders/OrderDetailsModal";
import RemoveItemsModal from "../Modal/orders/RemoveItemModal";
import Bill from "../Modal/accounting/BillModal";
import { Table, TableMenu, TableColumn, MenuOption } from "../components/Table";

import EmptyCartImg from "../assets/empty-cart.png";
import OrderItem from "../models/OrderItem";
import { OrderDetailModal, OrderHistoryDisplay, ItemToRemove } from "../types/order";

import { initAllStores } from "../utils/cache";
import { capitalizeFirst, padNumber } from "../utils/string";
import { formatDateTime } from "../utils/date";
import { PAGINATION_PAGE_NUMBER } from "../constants";
import { checkAccess, computeBookingOrderAccess } from "../utils/access";
import ReceiptModal from "../Modal/accounting/ReceiptModal";
import Room from "../models/Room";
import Order from "../models/Order";
import { terminal } from "../utils/user-helper";
import { syncBillHistory } from "../utils/sync";
// import { enrichOrderItems } from "../utils/helper-function";
// import { generatedBillNo } from "../utils/bill";

// ---------------- Table Columns ----------------
const columns: TableColumn[] = [
  { key: "orderNo", label: "Order No.", },
  { key: "createdAt", label: "Created At", responsive: "md" },
  {
    key: "customerName",
    label: "Customer",
    getClassName: (value) =>
      value === "Not Provided / N/A" || value === "Deleted Guest"
        ? "text-gray-400 italic"
        : "",
    responsive: "md",
  },

  { key: "room", label: "Room", responsive: "md", },
  { key: "paymentMethod", label: "Payment Method", responsive: "md", },
  // { key: "discount", label: "Discount",responsive: "md", },
  { key: "amount", label: "Amount" },
  {
    key: "paymentStatus",
    label: "Status",
    getClassName: (value) =>
      value === "Pending" ? "text-red-500 font-semibold" : "text-green-600 font-semibold",
  },
];

// ---------------- Main Component ----------------
const Orders: React.FC = () => {
  // Contexts
  const { cart, updateQuantity, removeFromCart, clearCart } = useCart();
  const { openModal, closeModal } = useModal();
  const location = useLocation() as { state?: { showHistory?: boolean } };

  const {
    identifier,
    setIdentifier,
    orderType,
    setOrderType,
    isPaid,
    setIsPaid,
    paymentMethod,
    setPaymentMethod,
    // discount,
    // setDiscount,
    // description,
    setDescription,
    placeholder,
    loading,
    showOrderConfirmPopUp,
    // syncOrder,
    loadOrderHistory,
    showClearOrderConfirmPopUp,
    showBill,
    setShowBill,
    orderBillData,
    loadOrderBill,
    showRemoveOrderModal,
    deletSelectedItems,
    orderHistory,
    setOrderHistory,
    ordersByDate,
    receiptData,
    showReceiptModal,
    setShowReceiptModal
  } = useOrder();

  const [terminalId] = useState(() => terminal());

  // Local state
  const [activeTab, setActiveTab] = useState<"items" | "history">("items");
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const [orderData, setOrderData] = useState<OrderDetailModal>({} as OrderDetailModal);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [orderItems, setOrderItems] = useState<ItemToRemove[]>([]);
  const [toDeleteOrderId, setToDeleteOrderId] = useState("");
  const [showFilter, setShowFilter] = useState(false);

  const [access, setAccess] = useState({ forceDeleteOrder: false });
  const [occupiedRooms, setOccupiedRooms] = useState<(Room & { guestName: string })[]>([]);
  const [activeOrders, setActiveOrders] = useState<(Order & { guestName: string })[]>([]);

  useEffect(() => {
    const getOccupiedRooms = async () => {
      const cache = await initAllStores();

      // Fetch all rooms currently marked as occupied
      const occupiedRooms: Room[] = await cache.getAllBy("rooms", {
        isOccupied: true, terminal: terminalId
      });

      // Map each occupied room with its active guest info
      const mappedRooms = await Promise.all(
        occupiedRooms.map(async (room) => {

          const bookedRoom = await cache.getBy("bookedRooms", { roomId: room.id });
          return {
            ...room,
            guestName: bookedRoom?.guestName ?? "Guest",
          };
        })
      );

      setOccupiedRooms(mappedRooms);
    };

    const getActiveOrder = async () => {
      const cache = await initAllStores();
      const activeOrders: Order[] = await cache.getAllBy("orders", { isPaid: false });

      const mappedOrders = await Promise.all(
        activeOrders.map(async (order) => {
          const bookedRoom = await cache.getBy("bookedRooms", { id: order.bookedRoomId });
          return {
            ...order,
            guestName: order.customer?.name
              ? order.customer?.name
              : bookedRoom?.guestName,
          };
        })
      );

      setActiveOrders(mappedOrders);
    }

    try {
      getActiveOrder();
      getOccupiedRooms();
    } catch (err: any) {
      console.log(err);
    }

  }, [activeTab])

  useEffect(() => {
    const accessGranted = computeBookingOrderAccess("orders");
    setAccess(accessGranted);
  }, [])

  useEffect(() => {
    if (location.state?.showHistory) {
      setActiveTab("history");
      loadOrderHistory();
    }
  }, [location.state]);

  // ---------------- Helpers ----------------
  const increaseQty = (item: OrderItem) => updateQuantity(item.id, item.quantity + 1);
  const decreaseQty = (item: OrderItem) => {
    if (item.quantity > 1) updateQuantity(item.id, item.quantity - 1);
  };
  const removeItem = (item: OrderItem) => removeFromCart(item.id);


  const total =
    cart.reduce((sum, item) => sum + item.amount * item.quantity, 0)


  // ---------------- Filtering ----------------
  const filteredCart = cart.filter((item) =>
    item.itemName.toLowerCase().includes(search.toLowerCase())
  );

  const filteredHistory = orderHistory.filter((order) => {
    const matchesSearch = (
      order.customerName.toLowerCase().includes(search.toLowerCase())
      || order.room.toLowerCase().includes(search.toLowerCase())
    );
    if (selectedCategory) {
      const matchesCategory =
        (selectedCategory === "All" && /* add date check */ true) ||
        (selectedCategory === "Pending" && order.paymentStatus === "Pending") ||
        (selectedCategory === "Paid" && order.paymentStatus === "Paid");
      return matchesSearch && matchesCategory;
    }
    return matchesSearch;
  });

  // ---------------- Order Details ----------------
  const orderDetails = async (orderId: string) => {
    const cache = await initAllStores();
    const order = await cache.getBy("orders", { id: orderId });

    const bookedRoom = await cache.getBy("bookedRooms", { id: order?.bookedRoomId });
    const room = await cache.getBy("rooms", { id: bookedRoom?.roomId });

    const getName = (user: { firstName: string; lastName: string } | null): string =>
      user ? `${user.firstName} ${user.lastName}` : "";
    // order.orderItems = await enrichOrderItems(order.orderItems);

    setOrderData({
      guestName: order.customer?.name
        ? `${order.customer?.name ?? "Deleted Guest"} / N/A`
        : `${bookedRoom?.guestName ?? "Deleted Guest"} / ${room?.number ?? "N/A"}`,
      purchaseDate: formatDateTime(order.createdAt),
      purchaseTime: new Date(order.createdAt).toDateString(),
      isPaidByGuest: order.isPaidByGuest,
      paymentStatus: order.isPaid ? "Paid" : "Pending",
      paymentMethod: capitalizeFirst(order.paymentMethod),
      orderNumber: padNumber(order.orderNo),
      discount: order.discount,
      description: order.description,
      orderedBy: {
        name: getName(order.orderedBy),
        portfolio: order.orderedBy?.portfolio,
      },
      clearedBy: order.clearedBy
        ? { name: getName(order.clearedBy), portfolio: order.clearedBy?.portfolio }
        : null,
      amount: order.amount,
      items: order.orderItems,
    });

    setIsModalOpen(true);
  };

  // ---------------- UI ----------------
  return (
    <div className="p-4">
      <DashboardHeader
        title="Order"
        categories={["Paid", "Pending",]}
        search={search}
        setSearch={setSearch}
        onCategorySelect={setSelectedCategory}
        showFilter={showFilter}
        filterByDate={true}
        onDateFilterApply={(start, end) => ordersByDate(start, end)}
      // onSync={loadOrderHistory}
      // lastSyncedAt={lastSyncedAt}
      />

      {/* Remove Items Modal */}
      {showRemoveModal && (
        <RemoveItemsModal
          title="Remove Items"
          items={orderItems}
          onConfirm={(selected) => {
            openModal(
              "Confirm Removal of Item(s)",
              "Are you sure you want to remove the selected order item(s)? This action cannot be undone.",
              () => deletSelectedItems(selected, toDeleteOrderId),
              true, "Yes, Remove", "Cancel", "red"
            );
            setShowRemoveModal(false);
          }}
          onClose={() => setShowRemoveModal(false)}
        />
      )}

      {/* Bill Modal */}
      {showBill && (
        <Bill
          billNumber={orderBillData.billNumber}
          hotel={orderBillData.hotel}
          discount={orderBillData.discount}
          guestName={orderBillData.guestName}
          issuedBy={orderBillData.issuedBy}
          bookings={[]}
          orders={orderBillData.orders}
          onClose={() => setShowBill(false)}
        />
      )}

      {/* Order Details Modal */}
      <OrderDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        {...orderData}
      />

      {showReceiptModal && (
        <ReceiptModal
          sample={receiptData}
          onClose={() => setShowReceiptModal(false)}
        />
      )}
      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        <FullPageSpinner show={loading} />
        <button
          onClick={() => {
            setActiveTab("items");
            setShowFilter(false);
          }}
          className={`px-4 py-2 rounded-lg font-semibold ${activeTab === "items" ? "bg-blue-600 text-white" : "bg-gray-200"
            }`}
        >
          Order Items
        </button>
        <button
          onClick={() => {
            loadOrderHistory();
            setActiveTab("history");
            setShowFilter(true);
          }}
          className={`px-4 py-2 rounded-lg font-semibold ${activeTab === "history" ? "bg-blue-600 text-white" : "bg-gray-200"
            }`}
        >
          Order History
        </button>
      </div>

      {/* Cart / Order Items */}
      {activeTab === "items" ? (

        <>
          {filteredCart.length === 0 ? (
            <div className="text-center mt-10 text-gray-500">
              {cart.length === 0 ? (
                <>
                  <img src={EmptyCartImg} alt="Empty cart" className="mx-auto w-60 mb-4" />
                  <p>Your cart is empty. Go to Services to add items.</p>
                </>
              ) : (
                <p>No items match your search.</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Item List */}
              <div className="lg:col-span-2 max-w-lg">
                <h2 className="text-xl font-semibold mb-4">Order Items</h2>
                <ul className="space-y-3">
                  {filteredCart.map((item: OrderItem) => (
                    <li
                      key={item.id}
                      className="flex justify-between items-center p-3 border rounded-lg shadow-sm"
                    >
                      <div>
                        <p className="font-medium">{item.itemName}</p>
                        <p className="text-sm text-gray-500">
                          ₦{item.amount.toFixed(2)} each
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        <button onClick={() => decreaseQty(item)} className="p-1 rounded bg-gray-200">
                          {FaMinus({ size: 14 })}
                        </button>
                        <span className="w-6 text-center">{item.quantity}</span>
                        <button onClick={() => increaseQty(item)} className="p-1 rounded bg-gray-200">
                          {FaPlus({ size: 14 })}
                        </button>

                        <span className="font-semibold w-20 text-right">
                          ₦{(item.amount * item.quantity).toFixed(2)}
                        </span>

                        <button
                          onClick={() => removeItem(item)}
                          className="text-red-500 hover:text-red-700"
                        >
                          {FaTrash({ size: 16 })}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Order Details */}
              <div className="p-4 border rounded-lg shadow-sm space-y-4">
                <h2 className="text-xl font-semibold mb-4">Order Details</h2>
                <div className="space-y-3">
                  <select
                    value={orderType}
                    onChange={(e) => {
                      const od = e.target.value as OrderType;

                      if (od === "room") syncBillHistory({ bookedRooms: [] });
                      // console.log(od)
                      setOrderType(od)
                    }}
                    className="w-full border p-2 rounded"
                  >
                    <option value="">Select Order Type</option>
                    <option value="new">Create New Order</option>
                    <option value="existing">Append to Current Order</option>
                    <option value="room">Assign to Guest Room</option>
                  </select>

                  {orderType === "new" && (
                    <input
                      type="text"
                      placeholder={placeholder}
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className="w-full border p-2 rounded"
                    />
                  )}

                  {orderType === "room" && (
                    <select
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className="w-full border p-2 rounded bg-white"
                    >
                      <option value="">{"Select Room"}</option>
                      {occupiedRooms.map((room) => (
                        <option key={room.id} value={room.number}>
                          Room {room.number.toUpperCase()} — {room.guestName}
                        </option>
                      ))}
                    </select>
                  )}

                  {orderType === "existing" && (
                    <select
                      value={identifier}
                      // onClick={async () => activeOrders()}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className="w-full border p-2 rounded bg-white"
                    >
                      <option value="">{"Select Order"}</option>
                      {activeOrders.map((o) => (
                        <option key={o.id} value={o.orderNo}>
                          Order {padNumber(o.orderNo as number)} — {o.guestName || "Guest"}
                        </option>
                      ))}
                    </select>
                  )}

                  {orderType !== "existing" && (
                    <>
                      <select
                        value={isPaid}
                        onChange={(e) => setIsPaid(e.target.value.trim())}
                        className="w-full border p-2 rounded"
                      >
                        <option value="">Payment Status</option>
                        <option value="true">Paid</option>
                        <option value="false">Pending</option>
                      </select>

                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value.trim() as PaymentType)}
                        className="w-full border p-2 rounded"
                      >
                        <option value="">Select Payment Method</option>
                        <option value="pos">POS</option>
                        <option value="transfer">Transfer</option>
                        <option value="cash">Cash</option>
                      </select>

                      <input
                        type="text"
                        placeholder="Description (optional)"
                        onChange={(e) => setDescription(e.target.value.trim())}
                        className="w-full border p-2 rounded"
                      />
                    </>
                  )}
                </div>

                <h2 className="text-lg font-semibold mt-4">Order Summary</h2>
                <div className="flex justify-between mt-2 mb-2">
                  <span className="text-gray-600">Total Amount</span>
                  <span className="font-bold">
                    ₦{(total < 1 ? 0 : total).toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={clearCart}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg"
                  >
                    Clear Cart
                  </button>
                  <button
                    onClick={() => showOrderConfirmPopUp()}
                    className="bg-primary text-white px-4 py-2 rounded-lg"
                  >
                    Place Order
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )
        : (
          <div className="space-y-4 mt-6">
            <Table
              columns={columns}
              data={filteredHistory}
              rowsPerPage={PAGINATION_PAGE_NUMBER}
              tableMenu={(row, closeMenu) => (
                <TableMenu
                  row={row}
                  options={[
                    {
                      label: "Details",
                      onClick: (r: OrderHistoryDisplay) => orderDetails(r.id),
                    },
                    {
                      label:
                        row.paymentStatus === "Pending"
                          ? "Print Bill" : "Print Receipt",
                      onClick: (r: OrderHistoryDisplay) => {
                        loadOrderBill(r.id);
                        // setShowBill(true);
                      },
                    },
                    row.paymentStatus === "Pending"
                      ? {
                        label: "Clear Bill",
                        color: "text-yellow-600",
                        onClick: (r: OrderHistoryDisplay) =>
                          showClearOrderConfirmPopUp(r.id, setOrderHistory),
                      }
                      : null,
                    {
                      label: "Remove Item",
                      color: "text-red-500",
                      onClick: (r: OrderHistoryDisplay) => {
                        // if (r.paymentStatus.toLowerCase() === "paid") {
                        const isAccess = checkAccess(
                          "forceDeleteOrder", access, openModal, closeModal
                        );
                        if (!isAccess) return;
                        // }
                        showRemoveOrderModal(r.id, setShowRemoveModal, setOrderItems);
                        setToDeleteOrderId(r.id);
                      },
                    },
                  ].filter(Boolean) as MenuOption[]}
                  closeMenu={closeMenu}
                />
              )}
            />
          </div>
        )}
    </div>
  );
};

export default Orders;